/* eslint-disable max-len */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import { convertTimeStampToEtDateTime, getNowDateTimeInET, convertDateTimeToUSLocaleStr } from '../helpers/timeHelpers';

import CheckoutCode from './CheckoutCode';
import Total from './Total';
import CheckoutCodeInput from './CheckoutCodeInput';
import getPriceSummaryValues from '../helpers/getPriceSummaryValues';
import ProductUnitSummary from './ProductUnitSummary';

const PriceSummaryBox = ({
  pressRelease,
  pressReleaseMetadata,
  sitesList = [],
  showCheckoutCodeInput,
  onToggleCheckoutCodeInput,
}) => {
  // We add this class to the `page-container` to allow for the sidebar to be sticky
  // Elements with `position: sticky` must have parent elements with `overflow: visible`
  // We weren't sure if we could remove `overflow: hidden` from `page-container`, so
  // we override it here with a class only when this element is loaded.
  useEffect(() => {
    document.getElementsByClassName('page-container')[0].classList.add('overflow-visible');
    return () => document.getElementsByClassName('page-container')[0].classList.remove('overflow-visible');
  });

  const {
    currentSite,
    selectedSites,
    selectedNewsletterAddOns,
    pricePerPressRelease,
    hasMultimediaAddons,
    totalPrice,
    codeDiscount,
  } = getPriceSummaryValues(pressRelease, pressReleaseMetadata, sitesList);

  const crosspostedSites = selectedSites.filter((site) => site.site_id !== pressReleaseMetadata.current_site_pk);
  const addonHeaders = selectedNewsletterAddOns.map((addon) => addon.top_header);
  const pressReleaseItemName = `Single press release on ${pressReleaseMetadata.current_site_name}`;

  const nowDateTimeInET = getNowDateTimeInET();
  const publishDateTime = convertTimeStampToEtDateTime(pressRelease.publish_date);
  let publishDateStr;
  if (!pressRelease.publish_date || (publishDateTime < nowDateTimeInET)) {
    publishDateStr = 'Publishing immediately';
  } else publishDateStr = convertDateTimeToUSLocaleStr(publishDateTime);

  return (
    <>
      {/* eslint-disable-next-line object-curly-newline */}
      <div className="price-summary-header">
        <span>Review order:</span>
        { onToggleCheckoutCodeInput && (
          <button onClick={onToggleCheckoutCodeInput} type="button" className="button button--soft promo-code-button">Have a promo code?</button>
        )}
      </div>
      <div className="price-summary-box">
        <div style={{ height: '32px' }} />
        <ProductUnitSummary
          unitTitle={pressReleaseItemName}
          detailStr={publishDateStr}
          pricePerItem={pricePerPressRelease}
          itemListPrice={pressReleaseMetadata.press_release_list_price}
          checkoutItems={[currentSite]}
        />
        {hasMultimediaAddons && (
          <ProductUnitSummary
            unitTitle="Multimedia add-on"
            pricePerItem={pressReleaseMetadata.multimedia_add_on_price}
            itemListPrice={pressReleaseMetadata.multimedia_add_on_price}
            checkoutItems={pressRelease.multimedia_add_ons}
            multiMediaAddOn
          />
        )}
        {(crosspostedSites.length > 0) && (
          <ProductUnitSummary
            unitTitle="Additional publications"
            pricePerItem={pricePerPressRelease}
            itemListPrice={pressReleaseMetadata.press_release_list_price}
            checkoutItems={crosspostedSites}
            minQuantityPerPackage={pressReleaseMetadata.crosspost_packages.map((pkg) => pkg.minimum_quantity)}
            crossPost
          />
        )}
        {(addonHeaders.length > 0) && (
          <ProductUnitSummary
            unitTitle="Additional topic newsletter"
            detailStr={addonHeaders.join(' | ')}
            pricePerItem={pressReleaseMetadata.topic_newsletter_add_on_price}
            itemListPrice={pressReleaseMetadata.topic_newsletter_add_on_price}
            checkoutItems={addonHeaders}
          />
        )}
        {pressRelease.checkout_code.name && (
          <div className="price-summary-box__multimedia-add-on">
            <span style={{ fontSize: '1.25rem', fontWeight: '500' }}>Promo code applied</span>
            <span style={{ color: 'red', fontSize: '1.25rem', fontWeight: '500' }}>- ${codeDiscount / 100}</span>
          </div>
        )}
        {showCheckoutCodeInput && (
          <>
            <CheckoutCodeInput />
            {pressRelease.checkout_code.name && !pressRelease.checkout_code.error && (
              <CheckoutCode code={pressRelease.checkout_code} />
            )}
          </>
        )}
        <div style={{ height: '32px' }} />
        <Total total={totalPrice} />
      </div>
    </>
  );
};

PriceSummaryBox.propTypes = {
  pressRelease: PropTypes.shape({
    title: PropTypes.string,
    publish_date: PropTypes.string,
    multimedia_add_ons: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        cloudinary_public_id: PropTypes.string,
        media_type: PropTypes.string,
        status: PropTypes.string,
        caption: PropTypes.string,
        file_name: PropTypes.string,
        embedded_video_link: PropTypes.string,
        secure_url: PropTypes.string,
      }),
    ),
    crosspost_sites: PropTypes.arrayOf(
      PropTypes.number,
    ),
    topic_newsletter_add_ons: PropTypes.arrayOf(
      PropTypes.number,
    ),
    checkout_code: PropTypes.shape({
      name: PropTypes.string,
      code_type: PropTypes.string,
      discount_amount: PropTypes.number,
      remaining_credits: PropTypes.number,
      error: PropTypes.string,
    }),
    price_per_press_release: PropTypes.number,
  }).isRequired,
  pressReleaseMetadata: PropTypes.shape({
    current_site_pk: PropTypes.number,
    current_site_name: PropTypes.string,
    press_release_list_price: PropTypes.number,
    topic_newsletter_add_on_price: PropTypes.number,
    multimedia_add_on_price: PropTypes.number,
    crosspost_packages: PropTypes.arrayOf(
      PropTypes.shape({
        pk: PropTypes.number,
        minimum_quantity: PropTypes.number,
        price_per_press_release: PropTypes.number,
        is_best_value: PropTypes.bool,
        is_most_popular: PropTypes.bool,
      }),
    ),
  }).isRequired,
  sitesList: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      site_name: PropTypes.string.isRequired,
      site_domain: PropTypes.string.isRequired,
      site_id: PropTypes.number.isRequired,
      order: PropTypes.number,
      suggested: PropTypes.bool.isRequired,
      topic_newsletter_add_on_options: PropTypes.arrayOf(
        PropTypes.shape({
          top_header: PropTypes.string,
          delivery_days: PropTypes.string,
          description: PropTypes.string,
          selected: PropTypes.bool,
        }),
      ),
    }),
  ),
  showCheckoutCodeInput: PropTypes.bool,
  onToggleCheckoutCodeInput: PropTypes.func,
};

export default PriceSummaryBox;
